<template>
  <div class="hub-page">
    <div v-if="selectionVisible" class="hub-main">
      <section class="merchant-card">
        <div class="merchant-row">
          <div class="merchant-avatar">
            <img :src="merchantIcon" alt="" />
          </div>
          <div class="merchant-meta">
            <div class="pay-title">{{ payTitle }}</div>
            <div class="merchant-name">{{ merchantDisplayName }}</div>
          </div>
        </div>
      </section>

      <section class="select-card">
        <div class="select-title">选择付款方式</div>
        <div class="option-list">
          <button
            v-for="item in payOptions"
            :key="item.code"
            type="button"
            class="pay-option"
            @click="chooseOption(item)"
          >
            <span class="brand-logo-wrap">
              <img class="brand-logo" :src="item.icon" :alt="item.label" />
            </span>
            <span class="brand-label">{{ item.label }}</span>
          </button>
        </div>
      </section>
    </div>

    <div v-else class="loading-wrap">
      <div class="spinner"></div>
      <p>{{ loadingText }}</p>
    </div>
  </div>
</template>

<script>
import { getPayOrderInfo, getRedirectUrl } from '@/api/api'
import config from '@/config'
import wayCodeUtils from '@/utils/wayCode'
import alipayLogoUrl from '../assets/payBrand/alipay.svg'
import wechatPayLogoUrl from '../assets/payBrand/wechat-pay.svg'

export default {
  data() {
    return {
      loadingText: '正在进入收银台',
      selectionVisible: false,
      payOrderInfo: {},
      payOptions: [],
      merchantName: '飞付通商户',
      merchantIcon: require('../assets/icon/merchant.svg'),
      loadingDelay: 0,
      loadingStartedAt: 0,
      loadingTimer: null
    }
  },

  computed: {
    merchantDisplayName() {
      return this.payOrderInfo.mchName || this.merchantName
    },

    payTitle() {
      return this.payOrderInfo.productQrc === true ? '商品收款' : '向商户付款'
    }
  },

  mounted() {
    this.loadingStartedAt = Date.now()
    this.loadingDelay = this.getLoadingDelay()
    this.dispatch()
  },

  beforeDestroy() {
    if (this.loadingTimer) {
      clearTimeout(this.loadingTimer)
    }
  },

  methods: {
    dispatch() {
      const payWay = wayCodeUtils.getPayWay()
      if (!payWay) {
        this.$router.push({ name: config.errorPageRouteName, params: { errInfo: '请使用微信或支付宝扫码付款' } })
        return
      }

      this.loadingText = '正在进入收银台'
      this.selectionVisible = false

      getPayOrderInfo(payWay.wayCode).then(orderInfo => {
        this.payOrderInfo = orderInfo || {}
        const supportWayCodes = this.getSupportWayCodes(orderInfo)

        this.runAfterLoading(() => {
          if (this.shouldShowSelection(payWay, orderInfo, supportWayCodes)) {
            this.selectionVisible = true
            this.payOptions = this.buildPayOptions(payWay)
            return
          }

          this.goCurrentOrExclusive(payWay, orderInfo, supportWayCodes)
        })
      }).catch(res => {
        this.$router.push({ name: config.errorPageRouteName, params: { errInfo: res.msg } })
      })
    },

    getLoadingDelay() {
      return 1000 + Math.floor(Math.random() * 501)
    },

    runAfterLoading(callback) {
      if (this.loadingTimer) {
        clearTimeout(this.loadingTimer)
      }

      const elapsed = Date.now() - this.loadingStartedAt
      const waitTime = Math.max(0, this.loadingDelay - elapsed)
      this.loadingTimer = setTimeout(callback, waitTime)
    },

    getSupportWayCodes(orderInfo) {
      return Array.isArray(orderInfo && orderInfo.supportWayCodes) ? orderInfo.supportWayCodes : []
    },

    hasWay(supportWayCodes, wayCode) {
      return supportWayCodes.indexOf(wayCode) >= 0
    },

    isStaticQrc(orderInfo) {
      return orderInfo.staticQrc === true || !orderInfo.payOrderId
    },

    hasExclusive(supportWayCodes) {
      return this.hasWay(supportWayCodes, config.payWay.EXCLUSIVE.wayCode)
    },

    canUseCurrentPay(payWay, orderInfo, supportWayCodes) {
      if (orderInfo.productQrc === true) {
        if (payWay.wayCode === config.payWay.WXPAY.wayCode) {
          return this.hasWay(supportWayCodes, config.payWay.WXPAY.wayCode)
        }
        return this.hasWay(supportWayCodes, config.payWay.ALIPAY.wayCode) ||
          this.hasWay(supportWayCodes, config.payWay.ALIPAY_QR.wayCode) ||
          this.hasWay(supportWayCodes, config.payWay.ALIPAY_OC.wayCode)
      }

      if (payWay.wayCode === config.payWay.WXPAY.wayCode) {
        return this.hasWay(supportWayCodes, config.payWay.WXPAY.wayCode)
      }

      const supportAliStaticQr = orderInfo.supportAliOrderQr === true ||
        this.hasWay(supportWayCodes, config.payWay.ALIPAY_QR.wayCode) ||
        (this.hasWay(supportWayCodes, config.payWay.ALIPAY_OC.wayCode) && orderInfo.supportAliOcForStaticQr !== false)

      if (!this.isStaticQrc(orderInfo) && this.hasWay(supportWayCodes, config.payWay.ALIPAY.wayCode)) {
        return true
      }

      return supportAliStaticQr || this.hasWay(supportWayCodes, config.payWay.ALIPAY.wayCode)
    },

    shouldShowSelection(payWay, orderInfo, supportWayCodes) {
      return this.hasExclusive(supportWayCodes) && this.canUseCurrentPay(payWay, orderInfo, supportWayCodes)
    },

    buildPayOptions(payWay) {
      const currentOption = payWay.wayCode === config.payWay.WXPAY.wayCode
        ? { code: 'wx', label: '微信支付', icon: wechatPayLogoUrl, action: 'current' }
        : { code: 'ali', label: '支付宝', icon: alipayLogoUrl, action: 'current' }

      return [
        currentOption,
        { code: 'exclusive', label: '专属通道', icon: this.merchantIcon, action: 'exclusive' }
      ]
    },

    chooseOption(item) {
      if (!item) {
        return
      }

      if (item.action === 'exclusive') {
        this.goExclusive()
        return
      }

      const payWay = wayCodeUtils.getPayWay()
      if (!payWay) {
        this.$router.push({ name: config.errorPageRouteName, params: { errInfo: '请使用微信或支付宝扫码付款' } })
        return
      }

      this.goCurrentPay(payWay, this.payOrderInfo, this.getSupportWayCodes(this.payOrderInfo))
    },

    goCurrentOrExclusive(payWay, orderInfo, supportWayCodes) {
      if (this.goCurrentPay(payWay, orderInfo, supportWayCodes)) {
        return
      }

      if (this.hasExclusive(supportWayCodes)) {
        this.goExclusive()
        return
      }

      if (payWay.wayCode === config.payWay.ALIPAY.wayCode) {
        this.$router.push({ name: config.errorPageRouteName, params: { errInfo: '该收款码未开启支付宝当面付，请联系商家' } })
        return
      }

      this.$router.push({ name: config.errorPageRouteName, params: { errInfo: '该收款码暂不支持微信单次扫码收款，请联系商家开通微信JSAPI支付' } })
    },

    goCurrentPay(payWay, orderInfo, supportWayCodes) {
      if (orderInfo.productQrc === true) {
        if (this.canUseCurrentPay(payWay, orderInfo, supportWayCodes)) {
          this.$router.push({ name: 'CashierProduct' })
          return true
        }
        return false
      }

      if (payWay.wayCode === config.payWay.ALIPAY.wayCode) {
        const supportAliStaticQr = orderInfo.supportAliOrderQr === true ||
          this.hasWay(supportWayCodes, config.payWay.ALIPAY_QR.wayCode) ||
          (this.hasWay(supportWayCodes, config.payWay.ALIPAY_OC.wayCode) && orderInfo.supportAliOcForStaticQr !== false)

        if (!this.isStaticQrc(orderInfo) && this.hasWay(supportWayCodes, config.payWay.ALIPAY.wayCode)) {
          this.redirectToOauth(config.payWay.ALIPAY.wayCode)
          return true
        }

        if (supportAliStaticQr) {
          this.$router.push({ name: config.payWay.ALIPAY.routeName })
          return true
        }

        if (this.hasWay(supportWayCodes, config.payWay.ALIPAY.wayCode)) {
          this.redirectToOauth(config.payWay.ALIPAY.wayCode)
          return true
        }

        return false
      }

      if (payWay.wayCode === config.payWay.WXPAY.wayCode && this.hasWay(supportWayCodes, config.payWay.WXPAY.wayCode)) {
        this.redirectToOauth(config.payWay.WXPAY.wayCode)
        return true
      }

      return false
    },

    goExclusive() {
      this.$router.push({
        name: config.payWay.EXCLUSIVE.routeName,
        params: { jeepayToken: this.$route.params.jeepayToken },
        query: { forceWeb: '1', onlyWay: 'exclusive' }
      })
    },

    redirectToOauth(payWayCode) {
      getRedirectUrl(payWayCode).then(res => {
        location.href = res
      }).catch(res => {
        this.$router.push({ name: config.errorPageRouteName, params: { errInfo: res.msg } })
      })
    }
  }
}
</script>

<style scoped>
.hub-page {
  min-height: 100vh;
  background: #f5f6f8;
  color: #1f2733;
  font-family: -apple-system, BlinkMacSystemFont, "PingFang SC", "Microsoft YaHei", sans-serif;
  -webkit-text-size-adjust: 100%;
}

.hub-main {
  padding: 42px 34px 64px;
}

.merchant-card,
.select-card {
  padding: 48px 44px 36px;
  border-radius: 24px;
  background: #fff;
  box-shadow: 0 18px 46px rgba(21, 52, 96, 0.07);
}

.merchant-row {
  display: flex;
  align-items: center;
}

.merchant-avatar {
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  width: 78px;
  height: 78px;
  border-radius: 22px;
  background: #edf4ff;
}

.merchant-avatar img {
  width: 46px;
  height: 46px;
}

.merchant-meta {
  min-width: 0;
  margin-left: 22px;
}

.pay-title {
  color: #7a8494;
  font-size: 24px;
}

.merchant-name {
  margin-top: 8px;
  overflow: hidden;
  color: #1f2733;
  font-size: 30px;
  font-weight: 600;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.select-card {
  margin-top: 24px;
}

.select-title {
  margin-bottom: 24px;
  color: #1f2733;
  font-size: 26px;
  font-weight: 700;
  text-align: center;
}

.option-list {
  display: flex;
  gap: 18px;
  justify-content: center;
}

.pay-option {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  width: 124px;
  height: 118px;
  border: 1px solid #e4eaf4;
  border-radius: 18px;
  background: #fff;
  box-shadow: 0 10px 24px rgba(21, 52, 96, 0.05);
}

.pay-option:active {
  transform: scale(0.98);
}

.brand-logo-wrap {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 44px;
  height: 44px;
}

.brand-logo {
  width: 44px;
  height: 44px;
  object-fit: contain;
}

.brand-label {
  margin-top: 10px;
  color: #334155;
  font-size: 18px;
  line-height: 1.2;
  text-align: center;
}

.loading-wrap {
  min-height: 100vh;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 40px;
  box-sizing: border-box;
}

.loading-wrap p {
  margin: 24px 0 0;
  color: #6b7480;
  font-size: 24px;
}

.spinner {
  width: 76px;
  height: 76px;
  border: 7px solid #dbe6f7;
  border-top-color: #2f80ed;
  border-radius: 50%;
  animation: spin 0.8s linear infinite;
}

@keyframes spin {
  to {
    transform: rotate(360deg);
  }
}
</style>
